import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderInline = (text) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p) ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>);

const Markdown = ({ text }) => {
  const lines = (text || "").split(/\r?\n/);
  const out = []; let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (!l.trim()) { i++; continue; }
    const h = l.match(/^(#{1,3})\s+(.*)$/);
    if (h) { out.push(<div key={out.length} className="font-semibold text-navy mt-2 mb-1">{renderInline(h[2])}</div>); i++; continue; }
    if (/^\s*[-*]\s+/.test(l)) {
      const items = []; while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, "")); i++; }
      out.push(<ul key={out.length} className="list-disc ms-5 my-1.5 space-y-1">{items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}</ul>); continue;
    }
    if (/^\s*\d+\.\s+/.test(l)) {
      const items = []; while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, "")); i++; }
      out.push(<ol key={out.length} className="list-decimal ms-5 my-1.5 space-y-1">{items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}</ol>); continue;
    }
    out.push(<p key={out.length} className="my-1.5 leading-relaxed">{renderInline(l)}</p>); i++;
  }
  return <div>{out}</div>;
};

export const TaxAssistant = () => {
  const { t, lang, isAr } = useLang();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [thread, busy, open]);

  const greeting = t({ en: "Hello! I'm the TANSEEQ Tax Assistant. Ask me about VAT, Corporate Tax, Excise or accounting in the UAE.", ar: "أهلاً! أنا مساعد تنسيق الضريبي. اسألني عن ضريبة القيمة المضافة أو الشركات أو الانتقائية أو المحاسبة في الإمارات." });

  const send = async () => {
    const msg = text.trim();
    if (!msg || busy) return;
    const next = [...thread, { role: "user", content: msg }];
    setThread(next); setText(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/assistant`, { message: msg, lang, history: thread.slice(-6) });
      setThread([...next, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setThread([...next, { role: "assistant", content: t({ en: "Sorry, I'm temporarily unavailable. Please try WhatsApp +971 56 115 2030.", ar: "عذراً، الخدمة غير متاحة مؤقتاً. يرجى التواصل عبر واتساب +971 56 115 2030." }) }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="assistant-launcher" aria-label="Tax Assistant"
        className="fixed bottom-24 end-6 z-40 h-14 w-14 grid place-items-center rounded-full bg-navy text-white shadow-lift hover:scale-105 hover:bg-navy-600 transition-transform duration-300">
        <Sparkles className="h-6 w-6 text-gold" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="assistant-panel"
            initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 end-6 z-[60] w-[calc(100%-3rem)] max-w-[400px] h-[560px] max-h-[80vh] flex flex-col rounded-2xl bg-white shadow-lift border border-line overflow-hidden">
            <div className="bg-navy-deep text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 grid place-items-center rounded-full bg-white/10"><Sparkles className="h-5 w-5 text-gold" /></div>
                <div className="leading-tight">
                  <div className="font-semibold text-sm">{t({ en: "TANSEEQ Tax Assistant", ar: "مساعد تنسيق الضريبي" })}</div>
                  <div className="text-[0.68rem] text-white/50">{t({ en: "AI · not a substitute for advice", ar: "ذكاء اصطناعي · لا يغني عن الاستشارة" })}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="assistant-close" className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-surface text-[0.86rem] text-ink">
              <div className="rounded-xl bg-white border border-line px-3.5 py-2.5 text-ink/80"><Markdown text={greeting} /></div>
              {thread.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`rounded-xl px-3.5 py-2.5 max-w-[85%] ${m.role === "user" ? "bg-navy text-white" : "bg-white border border-line text-ink/85"}`}>
                    {m.role === "user" ? m.content : <Markdown text={m.content} />}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="rounded-xl px-3.5 py-2.5 bg-white border border-line"><Loader2 className="h-4 w-4 animate-spin text-navy" /></div></div>}
            </div>

            <div className="p-3 border-t border-line bg-white shrink-0 flex items-end gap-2">
              <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="assistant-input" rows={1}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t({ en: "Ask a tax question…", ar: "اكتب سؤالك الضريبي…" })}
                className="flex-1 resize-none max-h-24 px-3 py-2.5 rounded-xl border border-line focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/10 text-sm" dir={isAr ? "rtl" : "ltr"} />
              <button onClick={send} disabled={busy} data-testid="assistant-send" className="h-10 w-10 grid place-items-center rounded-xl bg-navy text-white hover:bg-navy-600 disabled:opacity-50 transition-colors shrink-0">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
